import React, { useState, useEffect, useRef, useCallback } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';

// === Premium system refresh with cache-bust + service worker clear ===
const triggerSystemRefresh = async () => {
  try {
    // Unregister all service workers
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister().catch(() => {})));
    }
    // Clear all caches
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k).catch(() => {})));
    }
  } catch (_) { /* noop */ }
  // Hard reload with cache-bust param
  const url = new URL(window.location.href);
  url.searchParams.set('_t', String(Date.now()));
  window.location.replace(url.toString());
};import { useAuth } from '../context/AuthContext';
import { pushAPI, unreadAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import OnboardingTour from '../components/OnboardingTour';
import NotificationBell from '../components/NotificationBell';
import TrainingHintModal from '../components/TrainingHintModal';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuSeparator, 
  DropdownMenuTrigger 
} from '../components/ui/dropdown-menu';
import { Sheet, SheetContent, SheetTrigger } from '../components/ui/sheet';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  LayoutDashboard, 
  Users, 
  Plane, 
  CalendarDays, 
  CreditCard, 
  FileText, 
  Clock, 
  BarChart3, 
  LogOut, 
  Menu,
  ChevronRight,
  UserCircle,
  Award,
  Megaphone,
  ExternalLink,
  Gift,
  Wallet,
  GraduationCap,
  Building2,
  CalendarCheck,
  Palmtree,
  Settings,
  MessageSquare,
  UsersRound,
  Send,
  KeyRound,
  FolderOpen,
  FileSignature,
  TrendingUp,
  Banknote,
  Coins,
  GripVertical,
  Activity,
  RotateCcw,
  ShieldCheck,
  Archive,
  Inbox,
  Gauge,
  PiggyBank,
  ChevronDown,
  Crown
} from 'lucide-react';
import { ShieldAlert, Server } from 'lucide-react';

// ===== MENU SECTIONS =====
// Each item can have a `section` key. Items without section render at the top (root).
// Section meta: { key, label, icon, defaultOpen, roles? }
const SECTIONS = [
  { key: 'sprzedaz',     label: 'Sprzedaż',       icon: Plane },
  { key: 'finanse',      label: 'Finanse',        icon: PiggyBank },
  { key: 'biuro',        label: 'Biuro',          icon: FolderOpen },
  { key: 'komunikacja',  label: 'Komunikacja',    icon: Send },
  { key: 'franczyza',    label: 'Franczyza',      icon: Crown, roles: ['admin','branch_admin','kierownik'] },
  { key: 'hr',           label: 'HR & Zespół',    icon: UserCircle },
  { key: 'admin',        label: 'Administracja',  icon: Settings, roles: ['admin','kierownik'] },
];

// roles: which roles see this menu item; if undefined -> visible to all
// permission: module key — when set, item is also gated by user permissions (view+)
// section: which folder this item belongs to (null = root level)
// Admin (role='admin') always bypasses both filters.
const navItems = [
  // ROOT (no section)
  { to: '/', icon: LayoutDashboard, label: 'Panel główny' },
  { to: '/owner-dashboard', icon: Crown, label: 'Central Management', roles: ['admin'] },

  // SPRZEDAŻ
  { to: '/clients',         section: 'sprzedaz', icon: Users,        label: 'Klienci',         permission: 'clients' },
  { to: '/bookings',        section: 'sprzedaz', icon: CalendarDays, label: 'Rezerwacje',      permission: 'bookings' },
  { to: '/group-trips',     section: 'sprzedaz', icon: UsersRound,   label: 'Wyjazdy grupowe', permission: 'bookings' },
  { to: '/tour-operators',  section: 'sprzedaz', icon: Plane,        label: 'Touroperatorzy',  roles: ['admin','kierownik','branch_admin'] },
  { to: '/payments',        section: 'sprzedaz', icon: CreditCard,   label: 'Płatności',       permission: 'payments' },
  { to: '/vouchers',        section: 'sprzedaz', icon: Gift,         label: 'Bony wakacyjne',  permission: 'bookings' },
  { to: '/promotions',      section: 'sprzedaz', icon: Megaphone,    label: 'Promocje',        roles: ['admin'], permission: 'campaigns' },

  // FINANSE
  { to: '/finanse',                section: 'finanse', icon: PiggyBank, label: 'Dochód Netto',     roles: ['admin','branch_admin','kierownik'] },
  { to: '/invoices',               section: 'finanse', icon: FileText,  label: 'Faktury',          roles: ['admin','kierownik','branch_admin'], permission: 'invoices' },
  { to: '/settlements',            section: 'finanse', icon: Banknote,  label: 'Rozliczenia',      permission: 'settlements' },
  { to: '/rozliczenia-gotowki',    section: 'finanse', icon: Banknote,  label: 'Gotówka & Drobne', permission: 'settlements' },
  { to: '/cash-report',            section: 'finanse', icon: Wallet,    label: 'Raport kasowy',    permission: 'accounting' },
  { to: '/admin/costs',            section: 'finanse', icon: Wallet,    label: 'Koszty firmowe',   roles: ['admin','kierownik'], permission: 'accounting' },
  { to: '/reports',                section: 'finanse', icon: BarChart3, label: 'Raporty',          roles: ['admin','branch_admin'], permission: 'reports' },

  // BIURO
  { to: '/work-hours',  section: 'biuro', icon: Clock,         label: 'Czas pracy' },
  { to: '/leaves',      section: 'biuro', icon: Palmtree,      label: 'Urlopy',     permission: 'leaves' },
  { to: '/calendar',    section: 'biuro', icon: CalendarCheck, label: 'Kalendarz' },
  { to: '/documents',   section: 'biuro', icon: FolderOpen,    label: 'Dokumenty',  permission: 'documents' },
  { to: '/vault',       section: 'biuro', icon: KeyRound,      label: 'Sejf haseł' },

  // KOMUNIKACJA
  { to: '/messages',     section: 'komunikacja', icon: Send,           label: 'SMS / WhatsApp' },
  { to: '/chat-history', section: 'komunikacja', icon: MessageSquare,  label: 'Historia czatu', roles: ['admin'] },
  { to: '/campaigns',    section: 'komunikacja', icon: Megaphone,      label: 'Kampanie auto',  roles: ['admin','kierownik','branch_admin'], permission: 'campaigns' },

  // FRANCZYZA
  { to: '/franchise-settlements', section: 'franczyza', icon: Banknote,    label: 'Rozliczenia Franczyzowe', roles: ['admin','branch_admin','kierownik'] },
  { to: '/team-commissions',      section: 'franczyza', icon: Coins,       label: 'Prowizje Zespołu',         roles: ['admin','branch_admin','kierownik'] },
  { to: '/branch-dashboard',      section: 'franczyza', icon: Building2,   label: 'Panel Oddziału',           roles: ['branch_admin','kierownik'] },
  { to: '/branches',              section: 'franczyza', icon: Building2,   label: 'Oddziały',                 roles: ['admin'], permission: 'branches' },

  // HR & ZESPÓŁ
  { to: '/users',          section: 'hr', icon: UserCircle,    label: 'Pracownicy',      roles: ['admin','branch_admin'], permission: 'employees' },
  { to: '/contracts',      section: 'hr', icon: FileSignature, label: 'Umowy HR',        roles: ['admin','kierownik','branch_admin'], permission: 'contracts' },
  { to: '/employee-stats', section: 'hr', icon: TrendingUp,    label: 'Statystyki KPI',  permission: 'kpi' },
  { to: '/szkolenia',      section: 'hr', icon: GraduationCap, label: 'Szkolenia' },
  { to: '/commissions',    section: 'hr', icon: Award,         label: 'Premie',          roles: ['admin','branch_admin'], permission: 'bonuses' },
  { to: '/moja-prowizja',  section: 'hr', icon: Coins,         label: 'Moja prowizja' },

  // ADMINISTRACJA
  { to: '/settings',             section: 'admin', icon: Settings,    label: 'Ustawienia',       roles: ['admin'] },
  { to: '/audit-log',            section: 'admin', icon: ShieldAlert, label: 'Audit Log',        roles: ['admin'] },
  { to: '/ksef',                 section: 'admin', icon: Server,      label: 'KSeF 2.0',          roles: ['admin'] },
  { to: '/admin/system-status',  section: 'admin', icon: Activity,    label: 'Status Systemu',   roles: ['admin'], permission: 'system_status' },
  { to: '/admin/system-metrics', section: 'admin', icon: Gauge,       label: 'Performance',      roles: ['admin'], permission: 'system_status' },
  { to: '/admin/ocr-inbox',      section: 'admin', icon: Inbox,       label: 'OCR Inbox',        roles: ['admin','kierownik'], permission: 'accounting' },
  { to: '/admin/archive-center', section: 'admin', icon: Archive,     label: 'Archive Center',   roles: ['admin'], permission: 'archive' },
  { to: '/admin/invoices-qa',    section: 'admin', icon: ShieldCheck, label: 'Faktury QA',       roles: ['admin'], permission: 'invoices' },
  { to: '/factory-reset',        section: 'admin', icon: RotateCcw,   label: 'Factory Reset',    roles: ['admin'], danger: true },
];

const NavItem = ({ to, icon: Icon, label, onClick, danger, draggable, onDragStart, onDragOver, onDragLeave, onDrop, onDragEnd, isDragging, dragOver }) => (
  <div
    draggable={draggable}
    onDragStart={onDragStart}
    onDragOver={onDragOver}
    onDragLeave={onDragLeave}
    onDrop={onDrop}
    onDragEnd={onDragEnd}
    className={`group/nav relative ${isDragging ? 'opacity-40' : ''} ${dragOver ? 'border-t-2 border-amber-400' : ''}`}
  >
    <NavLink
      to={to}
      onClick={onClick}
      className={({ isActive }) =>
        `flex items-center gap-3 px-4 py-2.5 rounded-lg transition-all duration-200 text-sm ${
          isActive
            ? (danger
                ? 'bg-red-500/20 text-red-300 border-l-2 border-red-400 rounded-l-none font-semibold'
                : 'bg-gold-500/15 text-gold-400 border-l-2 border-gold-400 rounded-l-none font-semibold')
            : (danger
                ? 'text-red-300/80 hover:bg-red-500/10 hover:text-red-200'
                : 'text-navy-200 hover:bg-navy-700 hover:text-white')
        }`
      }
      data-testid={`nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <Icon className={`w-4 h-4 ${danger ? 'text-red-400' : ''}`} />
      <span className="flex-1">{label}</span>
      {draggable && (
        <GripVertical
          className="w-3.5 h-3.5 text-navy-400 opacity-0 group-hover/nav:opacity-100 transition cursor-grab active:cursor-grabbing"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
          title="Przeciągnij, żeby zmienić kolejność"
        />
      )}
    </NavLink>
  </div>
);

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) arr[i] = raw.charCodeAt(i);
  return arr;
}

const SIDEBAR_ORDER_KEY = 'aisha_sidebar_order_v1';

const Sidebar = ({ isAdmin, role, userPermissions, onItemClick, navigate, user, onSystemRefresh, deniedAlerts = 0 }) => {
  // Filter visible items based on role + permissions
  const visibleItems = React.useMemo(() => navItems
    .filter(item => !item.roles || item.roles.includes(role) || isAdmin)
    .filter(item => {
      if (isAdmin) return true;
      if (!item.permission) return true;
      const lv = (userPermissions || {})[item.permission];
      return lv && lv !== 'none';
    }), [isAdmin, role, userPermissions]);

  // Per-user localStorage key
  const storageKey = `${SIDEBAR_ORDER_KEY}_${user?.id || 'anon'}`;

  // Load saved order from localStorage, sort visibleItems accordingly
  const [orderedItems, setOrderedItems] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(storageKey) || '[]');
      if (!Array.isArray(saved) || saved.length === 0) return visibleItems;
      const byTo = new Map(visibleItems.map(it => [it.to, it]));
      const ordered = [];
      // Items in saved order first
      for (const to of saved) {
        if (byTo.has(to)) {
          ordered.push(byTo.get(to));
          byTo.delete(to);
        }
      }
      // Then any new items (added after saving) at the bottom
      for (const item of byTo.values()) ordered.push(item);
      return ordered;
    } catch (_) {
      return visibleItems;
    }
  });

  // Re-sync when role/permissions change (e.g. login as different user)
  useEffect(() => {
    setOrderedItems((prev) => {
      const toSet = new Set(visibleItems.map(i => i.to));
      const prevTos = new Set(prev.map(i => i.to));
      const sameSet = toSet.size === prevTos.size && [...toSet].every(t => prevTos.has(t));
      if (sameSet) return prev;
      // Different set — re-derive using saved order
      try {
        const saved = JSON.parse(localStorage.getItem(storageKey) || '[]');
        const byTo = new Map(visibleItems.map(it => [it.to, it]));
        const ordered = [];
        for (const to of saved) if (byTo.has(to)) { ordered.push(byTo.get(to)); byTo.delete(to); }
        for (const item of byTo.values()) ordered.push(item);
        return ordered;
      } catch (_) {
        return visibleItems;
      }
    });
  }, [visibleItems, storageKey]);

  const [dragIndex, setDragIndex] = useState(null);
  const [overIndex, setOverIndex] = useState(null);

  const persistOrder = (items) => {
    try { localStorage.setItem(storageKey, JSON.stringify(items.map(i => i.to))); } catch (_) { /* noop */ }
  };

  const handleDragStart = (idx) => (e) => {
    setDragIndex(idx);
    try { e.dataTransfer.effectAllowed = 'move'; } catch (_) { /* noop */ }
  };
  const handleDragOver = (idx) => (e) => {
    e.preventDefault();
    try { e.dataTransfer.dropEffect = 'move'; } catch (_) { /* noop */ }
    if (overIndex !== idx) setOverIndex(idx);
  };
  const handleDragLeave = () => () => { /* keep highlight until next over */ };
  const handleDrop = (idx) => (e) => {
    e.preventDefault();
    if (dragIndex === null || dragIndex === idx) {
      setDragIndex(null); setOverIndex(null);
      return;
    }
    setOrderedItems((prev) => {
      const next = [...prev];
      const [moved] = next.splice(dragIndex, 1);
      next.splice(idx, 0, moved);
      persistOrder(next);
      return next;
    });
    setDragIndex(null); setOverIndex(null);
  };
  const handleDragEnd = () => { setDragIndex(null); setOverIndex(null); };

  const handleResetOrder = () => {
    if (!window.confirm('Przywrócić domyślną kolejność menu?')) return;
    try { localStorage.removeItem(storageKey); } catch (_) { /* noop */ }
    setOrderedItems(visibleItems);
  };

  // === GROUP into sections (folders) ===
  // Build: rootItems[] (no section) + sectionsMap { key: items[] }
  const { rootItems, sectionsMap } = React.useMemo(() => {
    const root = [];
    const map = {};
    for (const it of orderedItems) {
      if (it.section) {
        if (!map[it.section]) map[it.section] = [];
        map[it.section].push(it);
      } else {
        root.push(it);
      }
    }
    return { rootItems: root, sectionsMap: map };
  }, [orderedItems]);

  // Sections visible based on RBAC + presence of any visible item
  const visibleSections = React.useMemo(() => SECTIONS
    .filter(s => !s.roles || s.roles.includes(role) || isAdmin)
    .filter(s => (sectionsMap[s.key] || []).length > 0), [sectionsMap, role, isAdmin]);

  // Persisted open/closed state per section (per-user)
  const sectionOpenKey = `aisha_sidebar_sections_v1_${user?.id || 'anon'}`;
  const [openSections, setOpenSections] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(sectionOpenKey) || 'null');
      if (saved && typeof saved === 'object') return saved;
    } catch (_) { /* noop */ }
    // Default: all sections expanded on first visit
    return Object.fromEntries(SECTIONS.map(s => [s.key, true]));
  });
  const toggleSection = (key) => {
    setOpenSections((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      try { localStorage.setItem(sectionOpenKey, JSON.stringify(next)); } catch (_) { /* noop */ }
      return next;
    });
  };

  return (
  <div className="flex flex-col h-full bg-navy-800">
    <button
      type="button"
      className="p-5 border-b border-navy-700 cursor-pointer w-full text-left group transition-all duration-200 ease-out hover:bg-navy-700/70 active:scale-[0.97] focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-400/50"
      onClick={() => (onSystemRefresh ? onSystemRefresh() : triggerSystemRefresh())}
      title="Kliknij aby odświeżyć system (cache bust)"
      data-testid="sidebar-logo-refresh"
    >
      <div className="flex items-center gap-3">
        <img
          src="/logo.png"
          alt="Aisha Travel"
          className="w-10 h-10 object-contain transition-transform duration-300 ease-out group-hover:scale-110 group-hover:rotate-[-3deg] group-active:scale-95"
          style={{ filter: 'drop-shadow(0 0 0 transparent)' }}
        />
        <div className="transition-transform duration-200 group-hover:translate-x-0.5">
          <h1 className="text-base font-bold text-gold-400 font-heading tracking-wide group-hover:text-gold-300 transition-colors">Aisha Travel</h1>
          <p className="text-[11px] text-navy-300 font-sans tracking-wider uppercase group-hover:text-navy-200 transition-colors">System CRM</p>
        </div>
      </div>
    </button>
    <ScrollArea className="flex-1 px-3 py-3">
      <nav className="space-y-0.5" data-testid="sidebar-nav">
        {/* ROOT items (Panel główny, Central Management) — draggable */}
        {rootItems.map((item, idx) => (
          <NavItem
            key={item.to}
            {...item}
            onClick={onItemClick}
            draggable
            isDragging={dragIndex === idx}
            dragOver={overIndex === idx && dragIndex !== null && dragIndex !== idx}
            onDragStart={handleDragStart(idx)}
            onDragOver={handleDragOver(idx)}
            onDragLeave={handleDragLeave(idx)}
            onDrop={handleDrop(idx)}
            onDragEnd={handleDragEnd}
          />
        ))}

        {/* SECTIONS (collapsible folders) */}
        {visibleSections.map((sec) => {
          const items = sectionsMap[sec.key] || [];
          const open = !!openSections[sec.key];
          const SecIcon = sec.icon;
          return (
            <div key={sec.key} className="mt-2" data-testid={`nav-section-${sec.key}`}>
              <button
                type="button"
                onClick={() => toggleSection(sec.key)}
                className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-[11px] uppercase tracking-wider font-semibold text-navy-300 hover:bg-navy-700/50 hover:text-gold-300 transition-colors"
                data-testid={`nav-section-toggle-${sec.key}`}
              >
                <SecIcon className="w-3.5 h-3.5" />
                <span className="flex-1 text-left">{sec.label}</span>
                <span className="text-[10px] text-navy-500 font-mono">{items.length}</span>
                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-0' : '-rotate-90'}`} />
              </button>
              {open && (
                <div className="pl-2 mt-0.5 space-y-0.5 border-l border-navy-700/60 ml-3" data-testid={`nav-section-items-${sec.key}`}>
                  {items.map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      onClick={onItemClick}
                      className={({ isActive }) =>
                        `flex items-center gap-3 pl-3 pr-4 py-2 rounded-lg transition-all duration-200 text-sm ${
                          isActive
                            ? (item.danger
                                ? 'bg-red-500/20 text-red-300 border-l-2 border-red-400 rounded-l-none font-semibold'
                                : 'bg-gold-500/15 text-gold-400 border-l-2 border-gold-400 rounded-l-none font-semibold')
                            : (item.danger
                                ? 'text-red-300/80 hover:bg-red-500/10 hover:text-red-200'
                                : 'text-navy-200 hover:bg-navy-700 hover:text-white')
                        }`
                      }
                      data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      <item.icon className={`w-4 h-4 ${item.danger ? 'text-red-400' : ''}`} />
                      <span>{item.label}</span>
                      {item.to === '/users' && isAdmin && deniedAlerts > 0 && (
                        <span
                          className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[10px] font-bold bg-red-500 text-white animate-pulse"
                          title={`${deniedAlerts} prób obejścia uprawnień w ostatnich 24 h`}
                          data-testid="denied-alerts-badge"
                        >
                          {deniedAlerts > 99 ? '99+' : deniedAlerts}
                        </span>
                      )}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </nav>
      <div className="mt-3 pt-3 border-t border-navy-700 space-y-2">
        <a
          href="/panel-klienta"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-navy-300 hover:bg-navy-700 hover:text-white transition-all text-sm"
        >
          <ExternalLink className="w-4 h-4" />
          <span>Panel klienta</span>
        </a>
        <button
          onClick={handleResetOrder}
          className="flex items-center gap-2 px-4 py-1.5 w-full rounded-lg text-navy-400 hover:bg-navy-700 hover:text-navy-200 transition-all text-[11px]"
          data-testid="reset-sidebar-order-btn"
          title="Przywróć domyślną kolejność menu"
        >
          <RotateCcw className="w-3 h-3" />
          <span>Przywróć kolejność menu</span>
        </button>
      </div>
    </ScrollArea>
    <div className="p-3 border-t border-navy-700">
      <div className="px-3 py-2 rounded-lg bg-navy-900/50 text-center">
        <p className="text-[10px] text-navy-400 tracking-wider">© 2026 Aisha Travel</p>
      </div>
    </div>
  </div>
  );
};

const Layout = () => {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [envInfo, setEnvInfo] = useState(null);
  const [userPermissions, setUserPermissions] = useState({});
  const [refreshing, setRefreshing] = useState(false);
  const [deniedAlerts, setDeniedAlerts] = useState(0);

  const handleSystemRefresh = useCallback(async () => {
    setRefreshing(true);
    // Small delay so user sees the overlay glow before page reloads
    setTimeout(() => { triggerSystemRefresh(); }, 450);
  }, []);
  const unreadInterval = useRef(null);
  const originalTitle = useRef('Aisha Travel CRM');

  // Fetch resolved permissions for current user (used by sidebar filter)
  useEffect(() => {
    if (!user) return;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/permissions/me?token=${localStorage.getItem('token')}`)
      .then(r => r.json())
      .then(data => setUserPermissions(data?.permissions || {}))
      .catch(() => setUserPermissions({}));
  }, [user]);

  // Admin-only: poll denied-alerts count for RBAC badge on "Pracownicy" link
  useEffect(() => {
    if (!user || !isAdmin) return;
    const load = () => {
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/permissions/denied-alerts?hours=24&token=${localStorage.getItem('token')}`)
        .then(r => r.json())
        .then(d => setDeniedAlerts(d?.count || 0))
        .catch(() => setDeniedAlerts(0));
    };
    load();
    const id = setInterval(load, 60_000);
    return () => clearInterval(id);
  }, [user, isAdmin]);

  // Fetch environment info to show DEV banner
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/env`)
      .then(r => r.json())
      .then(data => setEnvInfo(data))
      .catch(() => {});
  }, []);

  // Push notification subscription
  const subscribePush = useCallback(async () => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
    try {
      const reg = await navigator.serviceWorker.ready;
      const vapidRes = await pushAPI.getVapidKey();
      const publicKey = vapidRes.data.publicKey;
      if (!publicKey) return;
      const existing = await reg.pushManager.getSubscription();
      if (existing) return;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });
      const subJson = sub.toJSON();
      await pushAPI.subscribe({ endpoint: subJson.endpoint, keys: subJson.keys });
    } catch (err) {
      console.warn('Push subscription failed:', err);
    }
  }, []);

  // Unread counter
  useEffect(() => {
    const fetchUnread = async () => {
      try {
        const res = await unreadAPI.getCount();
        const c = res.data.count || 0;
        setUnreadCount(c);
        document.title = c > 0 ? `(${c}) ${originalTitle.current}` : originalTitle.current;
      } catch {}
    };
    fetchUnread();
    unreadInterval.current = setInterval(fetchUnread, 15000);
    return () => { clearInterval(unreadInterval.current); document.title = originalTitle.current; };
  }, []);

  // Request notification permission & subscribe
  useEffect(() => {
    if (!user) return;
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission().then((perm) => {
        if (perm === 'granted') subscribePush();
      });
    } else if ('Notification' in window && Notification.permission === 'granted') {
      subscribePush();
    }
  }, [user, subscribePush]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getInitials = (name) => {
    return name
      ?.split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase() || 'U';
  };

  const roleLabel = (role) => {
    const map = { admin: 'Administrator', branch_admin: 'Admin Oddziału', kierownik: 'Kierownik', pracownik: 'Pracownik' };
    return map[role] || role;
  };

  return (
    <div className="min-h-screen bg-background">
      {/* === Premium System Refresh Overlay === */}
      {refreshing && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center"
          style={{
            background: 'radial-gradient(ellipse at center, rgba(11,18,40,0.96) 0%, rgba(7,17,31,0.99) 70%)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
          }}
          data-testid="system-refresh-overlay"
        >
          {/* Ambient gold blobs */}
          <div className="absolute top-1/3 left-1/4 w-72 h-72 rounded-full opacity-20 blur-3xl" style={{ background: '#D4AF37' }} />
          <div className="absolute bottom-1/4 right-1/3 w-72 h-72 rounded-full opacity-10 blur-3xl" style={{ background: '#0891b2' }} />
          <div className="absolute inset-x-0 top-0 h-[3px]" style={{ background: 'linear-gradient(90deg, transparent, #D4AF37, transparent)' }} />

          <div className="relative text-center px-6">
            <div className="relative inline-block">
              <img
                src="/logo.png"
                alt="Aisha"
                className="w-16 h-16 sm:w-20 sm:h-20 mx-auto object-contain"
                style={{ animation: 'aisha-pulse 1.4s ease-in-out infinite' }}
              />
              {/* Spinning gold ring */}
              <div
                className="absolute inset-0 -m-3 rounded-full border-2 border-transparent"
                style={{
                  borderTopColor: '#D4AF37',
                  borderRightColor: 'rgba(212,175,55,0.4)',
                  animation: 'aisha-spin 1.2s linear infinite',
                }}
              />
            </div>
            <div className="mt-7" style={{ fontFamily: 'Georgia, serif', color: '#D4AF37', fontSize: 22, letterSpacing: '5px', fontWeight: 700 }}>
              AISHA TRAVEL
            </div>
            <div className="mt-1 text-[10px] sm:text-[11px] uppercase tracking-[4px] text-amber-200/70">Odświeżanie systemu</div>
            <div className="mt-5 flex items-center justify-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-300/90" style={{ animation: 'aisha-dot 1s ease-in-out infinite both' }} />
              <span className="w-1.5 h-1.5 rounded-full bg-amber-300/90" style={{ animation: 'aisha-dot 1s ease-in-out 0.16s infinite both' }} />
              <span className="w-1.5 h-1.5 rounded-full bg-amber-300/90" style={{ animation: 'aisha-dot 1s ease-in-out 0.32s infinite both' }} />
            </div>
            <div className="mt-5 text-[10px] tracking-[2px] text-slate-500 uppercase">Czyszczę cache · wczytuję najnowszą wersję</div>
          </div>

          <style>{`
            @keyframes aisha-pulse {
              0%, 100% { transform: scale(1); filter: drop-shadow(0 0 12px rgba(212,175,55,.45)); }
              50% { transform: scale(1.06); filter: drop-shadow(0 0 24px rgba(212,175,55,.7)); }
            }
            @keyframes aisha-spin { to { transform: rotate(360deg); } }
            @keyframes aisha-dot {
              0%, 80%, 100% { opacity: 0.25; transform: translateY(0); }
              40% { opacity: 1; transform: translateY(-3px); }
            }
          `}</style>
        </div>
      )}

      {envInfo?.is_development && (
        <div className="fixed top-0 left-0 right-0 z-[60] bg-red-600 text-white text-xs font-bold text-center py-1.5 shadow-lg tracking-wider uppercase" data-testid="dev-banner">
          <span className="inline-flex items-center gap-2">
            <span className="inline-block w-2 h-2 bg-white rounded-full animate-pulse"></span>
            DEV MODE — środowisko deweloperskie • emaile/SMS/scheduler mogą być wyłączone • dane testowe
            <span className="inline-block w-2 h-2 bg-white rounded-full animate-pulse"></span>
          </span>
        </div>
      )}
      <aside className={`hidden lg:fixed lg:inset-y-0 lg:left-0 lg:flex lg:w-60 lg:flex-col bg-navy-800 shadow-xl ${envInfo?.is_development ? 'lg:top-7' : ''}`}>
        <Sidebar isAdmin={isAdmin} role={user?.role} userPermissions={userPermissions} navigate={navigate} user={user} onSystemRefresh={handleSystemRefresh} deniedAlerts={deniedAlerts} />
      </aside>

      <header className="lg:hidden fixed top-0 left-0 right-0 h-14 bg-white border-b border-border z-50 flex items-center justify-between px-4 shadow-sm">
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" data-testid="mobile-menu-button">
              <Menu className="w-5 h-5 text-navy-800" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-60 p-0 bg-navy-800 border-0">
            <Sidebar isAdmin={isAdmin} role={user?.role} userPermissions={userPermissions} onItemClick={() => setMobileOpen(false)} navigate={navigate} user={user} onSystemRefresh={handleSystemRefresh} deniedAlerts={deniedAlerts} />
          </SheetContent>
        </Sheet>
        <button
          type="button"
          className="flex items-center gap-2 cursor-pointer group transition-transform duration-200 ease-out active:scale-95 focus:outline-none"
          onClick={handleSystemRefresh}
          title="Odśwież system (cache bust)"
          data-testid="mobile-logo-refresh"
        >
          <img src="/logo.png" alt="Aisha Travel" className="w-7 h-7 object-contain transition-transform duration-300 group-hover:scale-110 group-hover:rotate-[-3deg]" />
          <span className="font-bold text-navy-800 font-heading group-hover:text-gold-600 transition-colors">Aisha Travel</span>
        </button>
        <div className="flex items-center gap-1">
          <NotificationBell />
          <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="rounded-full mr-1" data-testid="mobile-user-menu">
              <Avatar className="w-8 h-8">
                <AvatarFallback className="bg-gold-100 text-gold-700 font-semibold text-xs">
                  {getInitials(user?.full_name)}
                </AvatarFallback>
              </Avatar>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuItem disabled className="font-medium">{user?.full_name}</DropdownMenuItem>
            <DropdownMenuItem disabled className="text-xs text-muted-foreground">{roleLabel(user?.role)}</DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => { navigate('/my-account'); setMobileOpen(false); }}>
              <Settings className="w-4 h-4 mr-2" />Moje konto
            </DropdownMenuItem>
            <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="mobile-logout-button">
              <LogOut className="w-4 h-4 mr-2" />Wyloguj się
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        </div>
      </header>

      <main className="lg:pl-60">
        <header className="hidden lg:flex h-14 items-center justify-between px-8 bg-white border-b border-border sticky top-0 z-40 shadow-sm">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span>Witaj,</span>
            <span className="font-semibold text-navy-800">{user?.full_name}</span>
            <span className={`ml-2 px-2.5 py-0.5 rounded-full text-[11px] font-semibold tracking-wide ${
              isAdmin ? 'bg-gold-100 text-gold-700' : user?.role === 'branch_admin' ? 'bg-purple-100 text-purple-700' : user?.role === 'kierownik' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600'
            }`}>
              {roleLabel(user?.role)}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <NotificationBell />
            <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="gap-2 hover:bg-navy-50" data-testid="desktop-user-menu">
                <Avatar className="w-8 h-8">
                  <AvatarFallback className="bg-gold-100 text-gold-700 font-semibold text-xs">
                    {getInitials(user?.full_name)}
                  </AvatarFallback>
                </Avatar>
                <ChevronRight className="w-4 h-4 text-muted-foreground" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <div className="px-2 py-1.5">
                <p className="font-medium text-sm">{user?.full_name}</p>
                <p className="text-xs text-muted-foreground">{user?.email}</p>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate('/my-account')}>
                <Settings className="w-4 h-4 mr-2" />Moje konto
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="desktop-logout-button">
                <LogOut className="w-4 h-4 mr-2" />Wyloguj się
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          </div>
        </header>

        <div className="pt-14 lg:pt-0 min-h-screen">
          <Outlet />
        </div>
      </main>
      <OnboardingTour />
      <TrainingHintModal />
    </div>
  );
};

export default Layout;
